import type { IDynamicComponentContainer } from '../components/app/dynamic-component-contracts';
import type { IPowerduckSystemResources } from './powerduck-system-resources';
import { Language } from '../common/enums/language';
import { ModalSectionMode, ModalSectionPillScrollMode } from '../common/enums/modal';
import StorageProvider from '../common/local-storage-shim';
import { isNullOrEmpty } from '../common/utils/is-null-or-empty';
import { documentWrap, globalState } from './global-state';

type PublicStaticMethods<T> = {
	[K in keyof T]: T[K] extends (...args: any[]) => any ? T[K] : never;
};

// Extract the public static methods from PowerduckState
export type PowerduckStateInitArgs = Partial<PublicStaticMethods<typeof PowerduckState>> & {
	keyPrefix: string;
};

export default class PowerduckState {
	private static _resources: IPowerduckSystemResources;
	private static _language: Language;
	private static _fullBlockerHtml = documentWrap?.querySelector('.loading-full-overlay')?.outerHTML || `<div class="loading-full-overlay"><div class="loading-inner"><div class="loading-indicator-wrap"><div class="holdon-white holdon-overlay holdon-element"><div class="holdon-content"><div class="sk-rect"><div class="rect1"></div><div class="rect2"></div><div class="rect3"></div><div class="rect4"></div><div class="rect5"></div></div></div></div></div></div></div>`;
	private static _blockerHtml = '<div class="holdon-white holdon-overlay holdon-element"><div class="holdon-content"><div class="sk-rect"><div class="rect1"></div><div class="rect2"></div><div class="rect3"></div><div class="rect4"></div><div class="rect5"></div></div></div></div>';
	private static _blockerSelector = '.holdon-overlay';
	private static _stickyMap: { [index: string]: IStickyDeclaration } = {};
	private static _prefix: string = '';

	static initialize(resources: IPowerduckSystemResources, args?: PowerduckStateInitArgs) {
		PowerduckState._resources = resources;
		PowerduckState._prefix = args.keyPrefix;

		if (args != null) {
			for (const key in args) {
				PowerduckState[key] = args[key];
			}
		}
	}

	static getIsDebugMode(): boolean {
		return null;
	}

	static getAppPrefix(): string {
		return PowerduckState._prefix;
	}

	static getCurrentLanguage(): Language {
		return this._language ?? Language.sk;
	}

	static getIntransparentBlockerHtml(): string {
		return PowerduckState._fullBlockerHtml;
	}

	static getBlockerHtml(): string {
		return PowerduckState._blockerHtml;
	}

	static getBlockerSelector(): string {
		return PowerduckState._blockerSelector;
	}

	static getResourceValue(key: keyof IPowerduckSystemResources) {
		if (this._resources != null) {
			return this._resources[key] || `{{${key}}}`;
		}

		return `{{${key}}}`;
	}

	static getCdnPath() {
		return '';
	}

	static getAssetRootDirectory() {
		return `${PowerduckState.getCdnPath()}/assets`;
	}

	static getFilesPath() {
		return '';
	}

	static getCurrentRouteQuery(): any {
		return null;
	}

	static getRouter(): any {
		return null;
	}

	static getBootstrapOnStyle(): 'primary' | 'info' {
		return 'primary';
	}

	static getFormControlCssClass(): string {
		return 'form-control maxwidth-input';
	}

	static getGeonamesUsername(): string {
		return null;
	}

	static getGoogleMapsApiKey(): string {
		return null;
	}

	static getModalSectionMode(): ModalSectionMode {
		if ((PowerduckState as any)._modalSectionMode == null) {
			if (StorageProvider.getString(`${PowerduckState._prefix}modalSectionMode`) != null) {
				(PowerduckState as any)._modalSectionMode = Number(StorageProvider.getString(`${PowerduckState._prefix}modalSectionMode`));
			} else {
				PowerduckState.setModalSectionMode(ModalSectionMode.navPills);
			}
		}

		return (PowerduckState as any)._modalSectionMode;
	}

	static setModalSectionMode(mode: ModalSectionMode): void {
		StorageProvider.setString(`${PowerduckState._prefix}modalSectionMode`, mode.toString());
		(PowerduckState as any)._modalSectionMode = mode;
	}

	/**
	 * How the side-pill section content should scroll when the user switches
	 * sections (navPills mode). Defaults to `rememberPosition` — i.e. enabled,
	 * restoring each section's last scroll offset and falling back to the top.
	 * Override at startup via `PowerduckState.initialize(..., { getModalSectionPillScrollMode })`
	 * or at runtime via `setModalSectionPillScrollMode`.
	 */
	static getModalSectionPillScrollMode(): ModalSectionPillScrollMode {
		return (PowerduckState as any)._modalSectionPillScrollMode ?? ModalSectionPillScrollMode.rememberPosition;
	}

	static setModalSectionPillScrollMode(mode: ModalSectionPillScrollMode): void {
		(PowerduckState as any)._modalSectionPillScrollMode = mode;
	}

	static parseErrorMessage(respText: string): string {
		let parsedErr: any;
		if (respText == null || respText.indexOf('{') != 0) {
			return respText;
		}

		try {
			parsedErr = JSON.parse(respText);
		} catch (error) {
			parsedErr = respText as any;
		}

		if (!isNullOrEmpty(parsedErr?.messages)) {
			let msgBuilder = '';
			parsedErr.messages.forEach((msgData) => {
				if (!isNullOrEmpty(msgData.message)) {
					if (msgBuilder.length > 0) {
						msgBuilder += '\n\n';
					}

					msgBuilder += msgData.message;
				}
			});

			return msgBuilder;
		}

		return null;
	}

	static get rootDynamicComponentContainer(): IDynamicComponentContainer {
		return globalState.RootDynamicContainerInstance;
	}

	static registerStickyTabs(id: string, state: IStickyDeclaration): void {
		PowerduckState._stickyMap[id] = state;
	}

	static unregisterStickyTabs(id: string): void {
		if (PowerduckState._stickyMap[id] != null) {
			try {
				delete PowerduckState._stickyMap[id];
			} catch (e) { }
		}
	}

	static get stickyTabs(): IStickyDeclaration {
		for (const id in PowerduckState._stickyMap) {
			if (PowerduckState._stickyMap[id].HasSticky) {
				return PowerduckState._stickyMap[id];
			}
		}

		return {
			AllowTabChange: true,
			HasSticky: false,
		};
	}
}

interface IStickyDeclaration {
	HasSticky: boolean;
	AllowTabChange: boolean;
}
